import React, {useContext, useEffect, useRef, useState} from "react"
import InputField from "../../InputField"
import PhoneInput from "react-phone-number-input"
import {capitalizeFirstLetter} from "../../../services/HelperService"
import {AccountType} from "../../../models/AccountType"
import {ReactComponent as PenBorderSvg} from "../../../assets/svg/pen-border.svg"
import {ReactComponent as PenSvg} from "../../../assets/svg/pen.svg"
import {ReactComponent as NextSvg} from "../../../assets/svg/arrow.svg"
import {ReactComponent as AddSvg} from "../../../assets/svg/plus.svg"
import {ReactComponent as TrashSvg} from "../../../assets/svg/trash.svg"
import ModalWidget from "../ModalWidget"
import {useLocation, useNavigate, useParams} from "react-router-dom"
import {useMenu} from "../../../contexts/MenuContext"
import {hasModalBackground} from "../../../hooks/useSafeBackNavigation"
import {handleInputChange} from "../../../utils/FormUtils"
import {getUser, listGuardiansForStudent, listWardsForGuardian} from "../../../services/UserService"
import FormInputError from "../../FormInputError"
import FormError from "../../FormError"
import SelectField from "../../SelectField";
import CheckboxWidget from "../../CheckboxWidget";
import DateField from "../../DateField";
import DropdownField from "../../DropdownField";
import SearchableDropdown, {SearchableOption} from "../../SearchableDropdown";
import {useUsers} from "../../../contexts/UsersContext";
import {useClassroom} from "../../../contexts/ClassroomContext";
import {SchoolUser} from "../../../types";
import {UserContext} from "../../../contexts/UserContext";
import { Collapse, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import {useUserMutations} from "../../../hooks/mutations/useUserMutations";
import {getApiErrors} from "../../../utils/apiError";
import {isIncompletePhoneNumber} from "../../../utils/phone";
import {isUpgradeError} from "../../../utils/upgradeError";
import UpgradeErrorMessage from "../../UpgradeErrorMessage";
import {useToast} from "../../../contexts/ToastContext";
import {useFormValidation} from "../../../hooks/useFormValidation";
import {userFormSchema, teacherFormSchema, studentFormSchema, guardianFormSchema, attachedGuardiansSchema, attachedStudentsSchema} from "../../../schemas/forms/user";
import {mapZodArrayErrors} from "../../../utils/formErrors";
import RelationshipTypeSelect from "../../RelationshipTypeSelect";
import {DEFAULT_RELATIONSHIP_TYPE} from "../../../constants/relationshipTypes";
import {createUserLoadOptions, formatStudentOption, formatGuardianOption} from "../../../utils/selectHelpers";
import {formatGuardianDisplayName} from "../../../utils/guardianDisplayName";
import {listClassrooms} from "../../../services/ClassroomService";
import {getBillingSubscription} from "../../../services/BillingService";
import {canInviteUserType, getInviteScope} from "../../../utils/settingsHelpers";
import {
    applyLinkChanges,
    computeLinkChanges,
    createInlineAttachedUsers,
} from "../../../utils/syncGuardianStudentLinks";

interface UserModalProps {
    show?: boolean;
    onHide?: () => void;
    userId?: string;
    userType?: string;
    attachedUserType?: string | null;
    classroomId?: string;
    classroomName?: string;
    termId?: string;
    onSuccess?: () => void;
}

interface UserForm {
    firstName: string;
    lastName: string;
    otherNames: string;
    email: string;
    gender: string;
    dob: string;
    phoneNo: string;
    [key: string]: any;
}

interface SelectOption {
    value: string;
    label: string;
}

interface AttachedLinkOption extends SelectOption {
    relationshipType: string;
}

function UserModal({
    show = true,
    onHide,
    userId: userIdProp,
    userType = AccountType.STUDENT,
    attachedUserType = null,
    classroomId,
    classroomName,
    termId,
    onSuccess
}: UserModalProps) {
    const navigate = useNavigate()
    const { navigateBack } = useMenu()
    const closeModal = onHide ?? navigateBack
    const finishSave = () => {
        if (hasModalBackground(location)) {
            navigateBack()
            return
        }
        if (onHide) {
            onHide()
            return
        }
        navigate('/' + userType + 's')
    }
    const capitalWord = capitalizeFirstLetter(userType)
    const attachedCapitalWord = capitalizeFirstLetter(attachedUserType)
    const {id: routeId} = useParams()
    const id = userIdProp ?? routeId
    const location = useLocation()
    const isEditRoute = location.pathname.endsWith('/edit')
    const {addNewUser, updateUser, triggerRefresh} = useUsers()
    const {setPendingStudentEnrollment} = useClassroom()
    const {addUserMutation, addStudentMutation, addGuardianMutation, editUserMutation} = useUserMutations()
    const {showSuccess, showError, showInfo} = useToast()
    const {validateForm} = useFormValidation()
    const [saveState, setSaveState] = useState<'idle' | 'saving' | 'completed'>('idle')
    const completedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const isTeacher = userType === AccountType.TEACHER
    const isStudent = userType === AccountType.STUDENT
    const isGuardian = userType === AccountType.GUARDIAN
    const isFromGradebook = !!(classroomId && classroomName)
    const formSchema = isTeacher ? teacherFormSchema : isStudent ? studentFormSchema : isGuardian ? guardianFormSchema : userFormSchema

    const [showExtraDetails, setShowExtraDetails] = useState(false)
    const [classroomOptions, setClassroomOptions] = useState<SearchableOption[]>(
        classroomId && classroomName ? [{ value: classroomId, label: classroomName }] : []
    )
    const [selectedClassroomId, setSelectedClassroomId] = useState<string | undefined>(classroomId)

    const [userData, setUserData] = useState<any>({})
    const [user, setUser] = useState<Record<string, any>>({
        firstName: "",
        lastName: "",
        otherNames: "",
        gender: "",
        dob: "",
        email: "",
        phoneNo: "",
    })
    const [error, setError] = useState<Record<string, string>>({})
    const [loading, setLoading] = useState<boolean>(false)
    const userCtx = useContext(UserContext)
    const schoolSettings = userCtx?.user?.school?.settings
    const schoolId = userCtx?.user?.school?.id ?? userCtx?.user?.school_id ?? ''
    const [planSendInvite, setPlanSendInvite] = useState<string[] | undefined>(undefined)
    const inviteScope = getInviteScope(planSendInvite, schoolSettings)
    const canInviteTeacher = canInviteUserType(AccountType.TEACHER, inviteScope)
    const [sendInvite, setSendInvite] = useState(false)

    const [selectedAttachedUsers, setSelectedAttachedUsers] = useState<AttachedLinkOption[]>([])
    const [originalLinkedUsers, setOriginalLinkedUsers] = useState<Record<string, string>>({})
    const [attachedUsersExist, setAttachedUsersExist] = useState<boolean>(false)
    const [showAttachedInfo, setShowAttachedInfo] = useState<boolean>(false)
    const [attachedCount, setAttachedCount] = useState<number>(1)

    const [attachedUsers, setAttachedUsers] = useState<UserForm[]>(Array.from({ length: 1 }, () => ({
        firstName: "",
        lastName: "",
        otherNames: "",
        email: "",
        gender: "",
        dob: "",
        phoneNo: "",
        relationshipType: DEFAULT_RELATIONSHIP_TYPE,
    })));

    useEffect(() => {
        if (id && isEditRoute) {
            getUserData(id);
        }

        getAttachedUsers()

        return () => {
            if (completedTimerRef.current) clearTimeout(completedTimerRef.current)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEditRoute]); // Call getUserData when id changes

    useEffect(() => {
        if (isStudent && show) {
            // Ensure the pre-selected classroom from gradebook is always available
            const fallback: SearchableOption[] = classroomId && classroomName
                ? [{ value: classroomId, label: classroomName }]
                : []
            listClassrooms(0, 200).then(res => {
                const opts = res.items.map(c => ({ value: c.id, label: c.name }))
                // Merge fallback if not already present
                if (classroomId && !opts.some(o => o.value === classroomId)) {
                    opts.unshift(fallback[0])
                }
                setClassroomOptions(opts)
            }).catch(() => {
                if (fallback.length) setClassroomOptions(fallback)
            })
        }
    }, [isStudent, show, classroomId, classroomName])

    // Sync selectedClassroomId when classroomId prop changes (e.g. switching classes in gradebook)
    useEffect(() => {
        setSelectedClassroomId(classroomId)
    }, [classroomId])

    useEffect(() => {
        if (!isTeacher || !show || !schoolId) {
            setPlanSendInvite(undefined)
            return
        }

        let isMounted = true
        getBillingSubscription(schoolId)
            .then(sub => {
                if (isMounted) {
                    setPlanSendInvite(sub.features?.send_invite)
                }
            })
            .catch(() => {
                if (isMounted) {
                    setPlanSendInvite(undefined)
                }
            })

        return () => {
            isMounted = false
        }
    }, [isTeacher, show, schoolId])

    useEffect(() => {
        setSendInvite(canInviteTeacher)
    }, [canInviteTeacher])

    const getUserData = async (id: string) => {
        setLoading(true)
        try {
            const data = await getUser(id) as any;
            setUserData(data)

            // Populate user state from fetched data for edit mode
            setUser({
                firstName: data.first_name || "",
                lastName: data.last_name || "",
                otherNames: data.other_names || "",
                gender: data.gender || "",
                dob: data.dob || "",
                email: data.email || "",
                phoneNo: data.phone_no || "",
            })

            if (attachedUserType) {
                let linkedOptions: AttachedLinkOption[] = [];

                if (isGuardian && attachedUserType === AccountType.STUDENT) {
                    const relationships = await listWardsForGuardian(id);
                    const relatedUsers = await Promise.all(
                        relationships.map(async (relationship) => {
                            try {
                                return await getUser(relationship.student_id);
                            } catch {
                                return null;
                            }
                        }),
                    );
                    const relatedUserMap = new Map(
                        relatedUsers
                            .filter((relatedUser): relatedUser is NonNullable<typeof relatedUser> => relatedUser != null)
                            .map((relatedUser) => [relatedUser.id, relatedUser]),
                    );
                    linkedOptions = relationships.map((relationship) => {
                        const relatedUser = relatedUserMap.get(relationship.student_id);
                        return {
                            value: relationship.student_id,
                            label: (relatedUser?.name ??
                                (relatedUser ? `${relatedUser.first_name} ${relatedUser.last_name}`.trim() : relationship.student_id)),
                            relationshipType: relationship.relationship_type ?? DEFAULT_RELATIONSHIP_TYPE,
                        };
                    });
                } else if (isStudent && attachedUserType === AccountType.GUARDIAN) {
                    const relationships = await listGuardiansForStudent(id);
                    const relatedUsers = await Promise.all(
                        relationships.map(async (relationship) => {
                            try {
                                return await getUser(relationship.guardian_id);
                            } catch {
                                return null;
                            }
                        }),
                    );
                    const relatedUserMap = new Map(
                        relatedUsers
                            .filter((relatedUser): relatedUser is NonNullable<typeof relatedUser> => relatedUser != null)
                            .map((relatedUser) => [relatedUser.id, relatedUser]),
                    );
                    linkedOptions = relationships.map((relationship) => {
                        const relatedUser = relatedUserMap.get(relationship.guardian_id);
                        return {
                            value: relationship.guardian_id,
                            label: formatGuardianDisplayName(relatedUser ?? {}, {
                                relationshipType: relationship.relationship_type ?? DEFAULT_RELATIONSHIP_TYPE,
                                childFirstName: data.first_name,
                                childLastName: data.last_name,
                            }),
                            relationshipType: relationship.relationship_type ?? DEFAULT_RELATIONSHIP_TYPE,
                        };
                    });
                }

                setAttachedUsersExist(linkedOptions.length > 0);
                setSelectedAttachedUsers(linkedOptions);
                setOriginalLinkedUsers(
                    Object.fromEntries(linkedOptions.map((item) => [item.value, item.relationshipType])),
                );
            }
        } catch (err) {
            //console.error(err);
            navigateBack()
        }finally {
            setLoading(false)
        }
    };

    const modalState = () => {
        if(isEditRoute){
            if(!showAttachedInfo){
                return {
                    title: 'Update ' + userData.first_name  + "'s Details",
                    actionTitle: attachedUserType ? 'Next' : 'Update ' + capitalWord,
                    backTitle: 'Close',
                    icon: attachedUserType ? NextSvg : PenSvg,
                    description: "Please fill in " + userType + " details",
                    action: ()=>validateUser(true),
                    onBack: closeModal,
                    navigateBack: true
                }
            }else{
                return {
                    title: 'Update ' + userData.first_name  + "'s " + attachedUserType + " details",
                    actionTitle: 'Update ' + capitalWord,
                    backTitle: 'Back',
                    icon: PenSvg,
                    description: "Please fill in " + userType + "'s " + attachedUserType + " details",
                    action: ()=>addUserDetails(true),
                    onBack: ()=>setShowAttachedInfo(false),
                    navigateBack: false
                }
            }
        }else{
            if(!showAttachedInfo){
                return {
                    title: 'Add ' + capitalWord + ' Manually',
                    actionTitle: attachedUserType ? 'Next' : 'Add ' + capitalWord,
                    backTitle: 'Change Method',
                    icon: attachedUserType ? NextSvg : PenBorderSvg,
                    description: "Please fill in " + userType + " details",
                    action: ()=>validateUser(false),
                    onBack: closeModal,
                    navigateBack: false
                }
            }else{
                return {
                    title: 'Add ' + userType + "'s " + attachedUserType,
                    actionTitle: 'Add ' + capitalWord,
                    backTitle: 'Back',
                    icon: PenSvg,
                    description: "Please fill in " + userType + "'s " + attachedUserType + " details",
                    action: ()=>addUserDetails(),
                    onBack: ()=>setShowAttachedInfo(false),
                    navigateBack: false
                }
            }
        }
    }
    const modalDetails = modalState()

    const genderOptions: string[] = [
        '',
        'Female',
        'Male',
        'Non-specify'
    ]

    const isFormValid = isStudent
        ? !!(user.firstName?.trim() && user.lastName?.trim())
        : isGuardian
        ? !!(user.email?.trim() || user.phoneNo?.trim())
        : !!(user.firstName?.trim() && user.lastName?.trim() && user.email?.trim())

    const handleValueChange = (value: any, name: string) => {
        handleInputChange(value, name, user, setUser)
        if (error[name] || error[name.replace(/([A-Z])/g, '_$1').toLowerCase()]) {
            const snakeName = name.replace(/([A-Z])/g, '_$1').toLowerCase()
            setError(prev => {
                const next = {...prev}
                delete next[name]
                delete next[snakeName]
                return next
            })
        }
    }

    const handleAttachedUserChange = (value: any, name: string, index: number) => {
        const updatedAttachedUsers = [...attachedUsers];
        updatedAttachedUsers[index] = {
            ...updatedAttachedUsers[index],
            [name]: value
        };
        setAttachedUsers(updatedAttachedUsers);
        setError({})
    }

    const onAddAttachedUser = () => {
        setAttachedCount(attachedCount + 1);
        setAttachedUsers(prevAttachedUsers => [
            ...prevAttachedUsers,
            {
                firstName: "",
                lastName: "",
                otherNames: "",
                email: "",
                gender: "",
                dob: "",
                phoneNo: "",
                relationshipType: DEFAULT_RELATIONSHIP_TYPE,
            }
        ]);
    }

    const handleSelectedAttachedUsersChange = (value: SelectOption[] | null) => {
        const nextSelected = value ?? [];
        setSelectedAttachedUsers((prev) => {
            const previousById = new Map(prev.map((item) => [item.value, item]));
            return nextSelected.map((option) => ({
                ...option,
                relationshipType: previousById.get(option.value)?.relationshipType ?? DEFAULT_RELATIONSHIP_TYPE,
            }));
        });
    };

    const updateSelectedAttachedUserType = (userId: string, relationshipType: string) => {
        setSelectedAttachedUsers((prev) =>
            prev.map((item) =>
                item.value === userId ? {...item, relationshipType} : item
            )
        );
    };

    const onRemoveAttachedUser = (index: number) => {
        setAttachedUsers(prev => prev.filter((_, i) => i !== index));
        setAttachedCount(prev => prev - 1);
    }

    const addUserDetails = async (updateExistingUser = false)=>{
        try {
            let existingIds: string[] = []
            let existingLinks: { guardian_id?: string; student_id?: string; relationship_type: string }[] = []
            let newAttached: any[] = []

            if(attachedUserType){
                if(attachedUsersExist){
                    existingIds = selectedAttachedUsers?.map(u => u.value) || []
                    if (!updateExistingUser && existingIds.length === 0) {
                        setError({
                            _global: `Select at least one ${attachedUserType}, or uncheck "${capitalizeFirstLetter(attachedUserType)}(s) accounts already exist" to add new ones.`,
                        });
                        return;
                    }
                    if (attachedUserType === AccountType.GUARDIAN) {
                        existingLinks = selectedAttachedUsers.map((item) => ({
                            guardian_id: item.value,
                            relationship_type: item.relationshipType,
                        }))
                    } else if (attachedUserType === AccountType.STUDENT) {
                        existingLinks = selectedAttachedUsers.map((item) => ({
                            student_id: item.value,
                            relationship_type: item.relationshipType,
                        }))
                    }
                }else{
                    const filledAttachedUsers = attachedUsers.filter((attached) =>
                        !!(attached.firstName?.trim() || attached.lastName?.trim() ||
                            attached.email?.trim() || !isIncompletePhoneNumber(attached.phoneNo))
                    );
                    if (filledAttachedUsers.length === 0) {
                        if (!updateExistingUser) {
                            const attachedSchema = attachedUserType === AccountType.GUARDIAN
                                ? attachedGuardiansSchema
                                : attachedStudentsSchema;
                            const result = attachedSchema.safeParse(attachedUsers);
                            if (!result.success) {
                                setError(mapZodArrayErrors(result, `${attachedUserType}s`));
                                return;
                            }
                            newAttached = result.data;
                        }
                    } else {
                        const attachedSchema = attachedUserType === AccountType.GUARDIAN
                            ? attachedGuardiansSchema
                            : attachedStudentsSchema;
                        const result = attachedSchema.safeParse(filledAttachedUsers);
                        if (!result.success) {
                            setError(mapZodArrayErrors(result, `${attachedUserType}s`));
                            return;
                        }
                        newAttached = result.data;
                    }
                }
            }

            let res: any
            if(!updateExistingUser){
                const validation = validateForm(formSchema, user);
                if (!validation.success) {
                    setError(validation.errors);
                    if (showAttachedInfo) {
                        setShowAttachedInfo(false);
                    }
                    return;
                }
                const userPayload = validation.data as Record<string, unknown>;

                // Route to the correct endpoint based on user type
                if(userType === AccountType.STUDENT){
                    const idKey = attachedUserType === AccountType.GUARDIAN ? 'guardianIds' : 'studentIds'
                    const linkKey = attachedUserType === AccountType.GUARDIAN ? 'guardianLinks' : 'studentLinks'
                    const inlineKey = attachedUserType === AccountType.GUARDIAN ? 'guardians' : 'students'
                    res = await addStudentMutation.mutateAsync({
                        ...userPayload,
                        sendInvite: isTeacher ? sendInvite : false,
                        sendInviteAttached: false,
                        [idKey]: existingIds,
                        [linkKey]: existingLinks,
                        [inlineKey]: newAttached,
                        ...(selectedClassroomId && { classroomId: selectedClassroomId }),
                        ...(termId && { termId }),
                    })
                }else if(userType === AccountType.GUARDIAN){
                    const idKey = attachedUserType === AccountType.STUDENT ? 'studentIds' : 'guardianIds'
                    const linkKey = attachedUserType === AccountType.STUDENT ? 'studentLinks' : 'guardianLinks'
                    const inlineKey = attachedUserType === AccountType.STUDENT ? 'students' : 'guardians'
                    res = await addGuardianMutation.mutateAsync({
                        ...userPayload,
                        sendInvite: isTeacher ? sendInvite : false,
                        sendInviteAttached: false,
                        [idKey]: existingIds,
                        [linkKey]: existingLinks,
                        [inlineKey]: newAttached,
                    })
                }else{
                    // Teacher or other types: use generic endpoint
                    res = await addUserMutation.mutateAsync({
                        userType: userType as any,
                        userDetails: { ...userPayload, sendInvite: isTeacher ? sendInvite : false },
                    })
                }
            }else{
                const validation = validateForm(formSchema, user);
                if (!validation.success) {
                    setError(validation.errors);
                    if (showAttachedInfo) {
                        setShowAttachedInfo(false);
                    }
                    return;
                }

                res = await editUserMutation.mutateAsync({
                    userId: id!,
                    userDetails: { ...(validation.data as Record<string, unknown>) },
                })

                if (attachedUserType && id) {
                    if (attachedUsersExist) {
                        await applyLinkChanges({
                            parentId: id,
                            isStudentMode: isStudent,
                            changes: computeLinkChanges(originalLinkedUsers, selectedAttachedUsers),
                        });
                    } else if (newAttached.length > 0) {
                        await createInlineAttachedUsers({
                            parentId: id,
                            isStudentMode: isStudent,
                            attachedUsers: newAttached,
                            addStudent: (payload) => addStudentMutation.mutateAsync(payload),
                            addGuardian: (payload) => addGuardianMutation.mutateAsync(payload),
                        });
                    }
                }
            }

            const userData = (res as any).data ?? res
            setSaveState('completed')

            // Show enrollment warnings if any
            const responseWarnings = (res as any)?.warnings ?? (userData as any)?.warnings;
            if (Array.isArray(responseWarnings) && responseWarnings.length > 0) {
                responseWarnings.forEach((w: string) => showInfo(w));
            }

            if(!updateExistingUser){
                const enrolledFromGradebook = isStudent && selectedClassroomId && termId;
                if (enrolledFromGradebook) {
                    setPendingStudentEnrollment({ classroomId: selectedClassroomId, termId });
                }
                if (!enrolledFromGradebook) {
                    triggerRefresh();
                }
                showSuccess(capitalWord + ' added successfully.')
                completedTimerRef.current = setTimeout(() => {
                    addNewUser(userData)
                    // Add inline attached users to context
                    if (attachedUserType && !attachedUsersExist) {
                        attachedUsers.filter(u => u.firstName.trim()).forEach(u => {
                            addNewUser({
                                id: crypto.randomUUID(),
                                email: u.email,
                                first_name: u.firstName,
                                last_name: u.lastName,
                                phone_no: u.phoneNo,
                                type: attachedUserType as any,
                                is_superuser: false,
                                is_active: true,
                                is_email_verified: false,
                                created_at: new Date().toISOString(),
                            } as SchoolUser)
                        })
                    }
                    if (onSuccess) {
                        onSuccess()
                        closeModal()
                    } else {
                        finishSave()
                    }
                }, 1500)
            }else{
                showSuccess(capitalWord + ' updated successfully.')
                completedTimerRef.current = setTimeout(() => {
                    updateUser(userData)
                    if (userCtx?.user?.id === id) {
                        userCtx.updateUser(userData)
                    }
                    triggerRefresh()
                    finishSave()
                }, 1500)
            }
        }
        catch(err: any)
        {
            const apiErrors = getApiErrors(err)
            setError(apiErrors)
            if (apiErrors._global) {
                showError(apiErrors._global)
            }
            // Navigate back to Step 1 if primary user fields have errors
            const hasPrimaryErrors = Object.keys(apiErrors).some(k => k !== '_global' && !k.includes('.'))
            const phoneRejected = /phone/i.test(apiErrors._global || '') || !!apiErrors.phone_no
            if ((hasPrimaryErrors || phoneRejected) && showAttachedInfo) {
                setShowAttachedInfo(false)
            }
        }
        finally
        {
            //setLoading(false)
        }
    }

    const validateUser = async (userExists: boolean)=>{
        try {
            // Validate form using schema
            const validation = validateForm(formSchema, user);
            if (!validation.success) {
                setError(validation.errors);
                return;
            }

            // Validation passed, move to next step or submit
            if (attachedUserType) {
                setError({});
                setShowAttachedInfo(true);
            } else {
                await addUserDetails(userExists);
            }
        }
        catch(err: any)
        {
            const apiErrors = getApiErrors(err)
            setError(apiErrors)
            if (apiErrors._global) {
                showError(apiErrors._global)
            }
        }
    }

    const getAttachedUsers = async ()=>{
        if(attachedUserType == null){return}
        // Options now loaded via async SelectField — no-op kept for backward compat
    }

    return (
        <ModalWidget
            headerIcon={PenBorderSvg}
            show={show}
            onHide={closeModal}
            onBack={modalDetails.onBack}
            title={modalDetails.title}
            description={modalDetails.description}
            actionTitle={modalDetails.actionTitle}
            actionIcon={modalDetails.icon}
            onAction={modalDetails.action}
            navigateBack={modalDetails.navigateBack}
            loading={loading}
            actionLoading={addUserMutation.isPending || addStudentMutation.isPending || addGuardianMutation.isPending || editUserMutation.isPending}
            actionCompleted={saveState === 'completed'}
            actionDisabled={!showAttachedInfo && !isFormValid}
            backButtonTitle={modalDetails.backTitle}>

            {error._global && (
                <FormError className="mb-3">
                    {isUpgradeError(error._global)
                        ? <UpgradeErrorMessage message={error._global} />
                        : error._global}
                </FormError>
            )}

            {isGuardian && !showAttachedInfo ? (
                <div className="font-xs text-muted mb-2"><span className="text-primary">*</span> Email or phone number is required</div>
            ) : !showAttachedInfo ? (
                <div className="font-xs text-muted mb-2"><span className="text-danger">*</span> Required</div>
            ) : null}

            {
                !showAttachedInfo?
                <>
                    <div className='row'>
                        <InputField className="col-6" label={capitalWord + "'s First Name"} placeholder="John"
                                    required={!isGuardian}
                                    errorMessage={error.first_name}
                                    onValueChange={(value: any)=>handleValueChange(value, "firstName")}
                                    name="firstName" initialValue={user.firstName}/>
                        <InputField className="col-6" label={capitalWord + "'s Last Name"} placeholder="Doe"
                                    required={!isGuardian}
                                    errorMessage={error.last_name}
                                    onValueChange={(value: any)=>handleValueChange(value, "lastName")}
                                    name="lastName" initialValue={user.lastName}/>
                    </div>

                    <InputField className='pt-2' label={capitalWord + "'s Email"} placeholder="contact@gradde.com"
                                required={!isStudent}
                                requiredColor={isGuardian ? "text-primary" : undefined}
                                errorMessage={error.email}
                                onValueChange={(value: any)=>handleValueChange(value, "email")}
                                name="email" initialValue={user.email}/>

                    {/* Classroom dropdown for students */}
                    {isStudent && !isEditRoute && (
                        <div className="pt-2">
                            <SearchableDropdown
                                label="Classroom"
                                placeholder="Select a classroom"
                                options={classroomOptions}
                                value={selectedClassroomId}
                                disabled={isFromGradebook}
                                onChange={(opt) => setSelectedClassroomId(opt?.value)}
                            />
                            {isFromGradebook && (
                                <div className="font-xs text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                    Auto-assigned from gradebook
                                </div>
                            )}
                        </div>
                    )}

                    {/* Guardian-only: Email and Phone always visible (required) */}
                    {isGuardian && (
                        <>
                            <div className="pt-2">
                                <div className="d-flex font-xs font-bold">{capitalWord + "'s  Phone Number"}<span className="text-primary ms-1">*</span></div>
                                <PhoneInput
                                    international
                                    className="w-100 my-2 w-100"
                                    defaultCountry="US"
                                    placholder="1234567890"
                                    value={user.phoneNo}
                                    onChange={(value: any)=>handleValueChange(value, "phoneNo")}/>
                                {error.phone_no && <FormInputError>{error.phone_no}</FormInputError>}
                            </div>
                        </>
                    )}

                    {/* Collapsible Extra Details for Students, Teachers, and Guardians */}
                    {(isStudent || isTeacher || isGuardian) && (
                        <div className="mt-3">
                            <button
                                type="button"
                                className="btn btn-link p-0 font-xs font-bold text-decoration-none"
                                onClick={() => setShowExtraDetails(!showExtraDetails)}
                                aria-expanded={showExtraDetails}
                            >
                                {showExtraDetails ? '▾' : '▸'} Extra Details
                            </button>
                            <Collapse in={showExtraDetails}>
                                <div>
                                    <InputField className='pt-2' label={capitalWord + "'s Other Names"}
                                                errorMessage={error.other_names}
                                                onValueChange={(value: any)=>handleValueChange(value, "otherNames")}
                                                placeholder="John Doe" name="otherNames" initialValue={user.otherNames}/>
                                    <div className='pt-2 row'>
                                        <DropdownField className="col-6" label="Gender" options={genderOptions}
                                                       value={user.gender}
                                                       errorMessage={error.gender}
                                                      onChange={(e: any)=>handleValueChange(e.value, "gender")}/>
                                        <DateField className="col-6" label={"Date of Birth"}
                                                    errorMessage={error.dob}
                                                    onValueChange={(value: any) => handleValueChange(value, "dob")}
                                                    name="dob" initialValue={user.dob}/>
                                    </div>
                                    {!isGuardian && (
                                        <div className="pt-2">
                                            <div className="d-flex font-xs font-bold">{capitalWord + "'s  Phone Number"}</div>
                                            <PhoneInput
                                                international
                                                className="w-100 my-2 w-100"
                                                defaultCountry="US"
                                                placholder="1234567890"
                                                value={user.phoneNo}
                                                onChange={(value: any)=>handleValueChange(value, "phoneNo")}/>
                                            {error.phone_no && <FormInputError>{error.phone_no}</FormInputError>}
                                        </div>
                                    )}
                                </div>
                            </Collapse>
                        </div>
                    )}

                    {!isEditRoute && isTeacher && (
                        <div className="mt-3">
                            {!canInviteTeacher ? (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip id="tooltip-invite-primary">Your current plan does not support inviting teachers</Tooltip>}
                                >
                                    <div style={{ display: 'inline-flex' }}>
                                        <Form.Check type="switch" id="send-invite-user" label="Send invite to teacher" checked={false} disabled />
                                    </div>
                                </OverlayTrigger>
                            ) : (
                                <div>
                                    <Form.Check type="switch" id="send-invite-user" label="Send invite to teacher" checked={sendInvite} onChange={e => setSendInvite(e.target.checked)} />
                                </div>
                            )}
                        </div>
                    )}
                </>:
                <>
                    <CheckboxWidget label={attachedCapitalWord + "(s) accounts already exist."}
                                    isChecked={attachedUsersExist}
                                    onValueChange={()=>setAttachedUsersExist(!attachedUsersExist)} />
                    {
                        attachedUsersExist?
                            <>
                            <SelectField className="mt-3" label={attachedCapitalWord + '(s) Name or ID'}
                                         loadOptions={attachedUserType ? createUserLoadOptions(attachedUserType as any) : undefined}
                                         formatOptionLabel={attachedUserType === AccountType.STUDENT ? formatStudentOption : attachedUserType === AccountType.GUARDIAN ? formatGuardianOption : undefined}
                                         fieldType={attachedUserType ?? undefined}
                                         placeholder="John Doe"
                                         onValueChange={handleSelectedAttachedUsersChange}
                                         initialValue={selectedAttachedUsers}
                            />
                            {selectedAttachedUsers.length > 0 && (
                                <div className="mt-3 d-flex flex-column gap-2">
                                    {selectedAttachedUsers.map((item) => (
                                        <div key={item.value} className="p-3" style={{border: '1px solid #E8DCF8', borderRadius: '16px'}}>
                                            <div className="font-sm font-bold mb-2">{item.label}</div>
                                            <RelationshipTypeSelect
                                                value={item.relationshipType}
                                                onChange={(relationshipType) => updateSelectedAttachedUserType(item.value, relationshipType)}
                                                label="Relationship type"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                            </>

                            :
                            <div className="mt-3">
                                {attachedUserType === AccountType.GUARDIAN && (
                                    <div className="font-xs text-muted mb-2"><span className="text-primary">*</span> Email or phone number is required</div>
                                )}
                                {/*attachedUsers form*/}
                                {Array.from({ length: attachedCount }).map((_, index) => (

                                    <div key={index}>
                                        {
                                            attachedCount > 1 &&
                                            <div className="d-flex justify-content-between align-items-center mb-2 mt-3">
                                                <span>{attachedCapitalWord + ' ' + (index + 1)}</span>
                                                <button type="button" className="btn btn-sm p-1" style={{color: '#dc3545'}} onClick={() => onRemoveAttachedUser(index)} title="Remove">
                                                    <TrashSvg width={18} height={18} />
                                                </button>
                                            </div>
                                        }

                                        <div className='row'>
                                            <InputField className="col-6"
                                                        label={attachedCapitalWord + "'s First Name"} placeholder="John"
                                                        required={attachedUserType !== AccountType.GUARDIAN}
                                                        name="firstName"
                                                        errorMessage={error[`${attachedUserType}s.${index}.first_name`]}
                                                        onValueChange={(value: any) => handleAttachedUserChange(value, "firstName", index)}
                                                        initialValue={attachedUsers[index]?.firstName}
                                            />
                                            <InputField className="col-6"
                                                        label={attachedCapitalWord + "'s Last Name"} placeholder="Doe"
                                                        required={attachedUserType !== AccountType.GUARDIAN}
                                                        errorMessage={error[`${attachedUserType}s.${index}.last_name`]}
                                                        onValueChange={(value: any) => handleAttachedUserChange(value, "lastName", index)}
                                                        name="lastName"
                                                        initialValue={attachedUsers[index]?.lastName}/>
                                        </div>

                                        <InputField className='pt-2' label={attachedCapitalWord + "'s Other Names"}
                                                    placeholder="John Doe" name="otherNames"
                                                    errorMessage={error[`${attachedUserType}s.${index}.other_names`]}
                                                    onValueChange={(value: any) => handleAttachedUserChange(value, "otherNames", index)}
                                                    initialValue={attachedUsers[index]?.otherNames}
                                        />
                                        <InputField className='pt-2' label={attachedCapitalWord + "'s Email"}
                                                    required={attachedUserType !== AccountType.STUDENT}
                                                    requiredColor={attachedUserType === AccountType.GUARDIAN ? "text-primary" : undefined}
                                                    placeholder="contact@gradde.com" name="email"
                                                    errorMessage={error[`${attachedUserType}s.${index}.email`]}
                                                    onValueChange={(value: any) => handleAttachedUserChange(value, "email", index)}
                                                    initialValue={attachedUsers[index]?.email}
                                        />

                                        <div className='pt-2 row'>
                                            <DropdownField className="col-6" label="Gender" options={genderOptions}
                                                        value={attachedUsers[index]?.gender || ''}
                                                        errorMessage={error[`${attachedUserType}s.${index}.gender`]}
                                                        onChange={(e: any) => handleAttachedUserChange(e.value, "gender", index)} />
                                            <DateField className="col-6" label={"Date of Birth"}
                                                        errorMessage={error[`${attachedUserType}s.${index}.dob`]}
                                                        onValueChange={(value: any) => handleAttachedUserChange(value, "dob", index)}
                                                        name="dob"
                                                        initialValue={attachedUsers[index]?.dob} />
                                        </div>

                                        <div className="pt-2">
                                            <div
                                                className="d-flex font-xs font-bold">{attachedCapitalWord + "'s  Phone Number"}{attachedUserType === AccountType.GUARDIAN && <span className="text-primary ms-1">*</span>}</div>
                                            <PhoneInput
                                                international
                                                className="w-100 my-2 w-100"
                                                defaultCountry="US"
                                                placholder="1234567890"
                                                value={attachedUsers[index]?.phoneNo}
                                                onChange={(value: any) => handleAttachedUserChange(value, "phoneNo", index)}/>
                                            {
                                                error[`${attachedUserType}s.${index}.phone_no`] &&
                                                <FormInputError>{error[`${attachedUserType}s.${index}.phone_no`]}</FormInputError>
                                            }
                                        </div>

                                        <div className="pt-2">
                                            <RelationshipTypeSelect
                                                value={attachedUsers[index]?.relationshipType ?? DEFAULT_RELATIONSHIP_TYPE}
                                                onChange={(value) => handleAttachedUserChange(value, "relationshipType", index)}
                                                label="Relationship type"
                                            />
                                        </div>

                                        {/* Profile picture — commented out for now
                                        {
                                            attachedUserType === AccountType.STUDENT &&
                                            <ImageUploadWidget label= {attachedCapitalWord + "'s Profile Picture"} className="pt-2"
                                                               errorMessage={error[`${attachedUserType}s.${index}.image`]}
                                                               onValueChange={(value: any) => handleAttachedUserChange(value, "image", index)}/>
                                        }
                                        */}
                                    </div>

                                ))}


                                <div className="d-flex justify-content-center">
                                    <button type="button" className="btn transparent-action-btn text-nowrap d-flex"
                                            onClick={onAddAttachedUser}>
                                        <AddSvg/>
                                        {"Add another " + attachedUserType}
                                    </button>
                                </div>
                            </div>
                    }
                </>
            }


        </ModalWidget>

    )
}

export default UserModal
